/* eslint-disable max-len */
import { Logger } from '@deqo/foundation';
import React, { DragEvent } from 'react';
import styled from 'styled-components';
import { CommandDispatcher } from '../../../concepts/commands/CommandDispatcher';
import { Point } from '../../../platform/geom/Point';
import { Fill } from '../../../ui/Fill';
import { ContainerScope, resolve } from '../../Container';
import { alert } from '../../packages/alerts/AlertCommands';
import { PopupManager } from '../../popups/PopupManager';
import { AdapterInfo } from '../AdapterInfo';
import { Camera } from '../Camera';
import { EditorSession } from '../EditorSession';
import { StatePropertyRef, createObserver } from '../ExtendableStore';
import { ContentInjector } from '../injection/ContentInjector';
import { ContentPayload } from '../injection/ContentPayload';
import { HeroIcons, Listener } from './../../../ui';

const errLog = Logger.get('app.errors');

const DropLayer = styled(Fill)`
    z-index: 20;
    background: rgba(0, 0, 0, 0.3);

    & * {
        pointer-events: none;
    }
`;

const DropIndicator = styled.div`
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    position: absolute;
    left: 50%;
    top: 50%;
    border-radius: 50%;
    width: 14rem;
    height: 14rem;
    transform: translate(-7rem, -7rem);
    background: rgba(0, 0, 0, 0.3);
    
    & > div {
        font-size: 3rem;
    }

    & > svg {
        font-size: 6rem;
    }
`;

export const IsDropping = StatePropertyRef<boolean>('isDropping');

const IsDroppingObserver = createObserver(IsDropping);

type DropItem = readonly [string, Blob | File];

export class DropSupport extends React.Component {

    public static contextType = ContainerScope;

    @resolve(Camera) private camera!: Camera;
    @resolve(CommandDispatcher) private dispatch!: CommandDispatcher;
    @resolve(ContentInjector) private injector!: ContentInjector;
    @resolve(PopupManager) private popups!: PopupManager;
    @resolve(EditorSession) private session!: EditorSession;

    public render() {
        return <>
            <Listener target={document.body} event="dragenter" callback={this.onDragEnter as any} />
            <IsDroppingObserver store={this.session} render={active => active && <>
                <DropLayer onMouseDown={this.onMouseDown} onDragOver={this.onDragOver} onDragLeave={this.onDragLeave} onDrop={this.onDragDrop}>
                    {<DropIndicator>
                        <HeroIcons.ArrowDown />
                        <div>Drop</div>
                    </DropIndicator>}
                </DropLayer>
            </>} />
        </>;
    }

    private onMouseDown = () => {
        // If for some reason the drop overlay gets stuck, clicking should dismiss it.
        this.session.update(IsDropping, false);
    };

    private onDragEnter = (e: DragEvent<HTMLElement>) => {
        const { items } = e.dataTransfer!;
        e.preventDefault();

        // If there is a full screen popup, do nothing
        if (this.popups.current?.fullscreen) return;
        // If there is a droppable popup, do nothing
        if (this.popups.current?.droppable) return;

        if (this.shouldAllowDrop(items)) {
            this.session.update(IsDropping, true);
            e.dataTransfer!.dropEffect = 'copy';

            this.popups.dismiss();
        }
    };

    private onDragOver = (e: DragEvent<HTMLElement>) => {
        e.preventDefault();
    };

    private onDragLeave = (e: DragEvent<HTMLElement>) => {
        e.preventDefault();
        this.session.update(IsDropping, false);
    };

    private onDragDrop = async (e: DragEvent) => {
        e.preventDefault();
        this.session.update(IsDropping, false);

        const vpt = this.camera.toViewFromScreen(Point.from(e, 'client'));
        const mpt = this.camera.toModel(vpt);

        const items = this.readRelevantDropItems(e.dataTransfer!);

        for (const [type, file] of items) {
            const payload = ContentPayload.create(type, file, { position: mpt, method: 'dragdrop' });
            if (await this.injector.inject(payload)) {
                return;
            }
        }

        this.dispatch(alert, ['Sorry, this file type is not supported at the moment.']);
    };

    private readRelevantDropItems = (dt: DataTransfer): DropItem[] => {
        // We support one file drop for now, but if its a "string" drop them it can have multiple relevant entries.

        if (dt.files.length) {
            const file = dt.files.item(0);
            if (file) return [[file.type, file]];
        }

        const strings = [] as DropItem[];

        for (const type of dt.types) {
            const data = dt.getData(type);
            const file = new Blob([data], { type });
            strings.push([type, file]);
        }

        // For strings, return all in reverse order (most important seem to be first).
        return strings.reverse();
    };

    private shouldAllowDrop(items: DataTransferItemList) {
        // Always allow file drop
        if (!!Array.from(items).find(x => x.kind === 'file')) return true;
        // Otherwise ban if popup active OR if adapter active
        if (this.session.get(AdapterInfo).target !== 'none') return false;
        if (!!this.popups.current) return false;
        return true;
    }
}

